// MARK://dhk8/admin-shell/lib/field-docs
// Schema-driven contextual docs por campo del catálogo (catalog-builder review
// item A2). Cada entrada `summary + examples + commonMistakes` se renderiza
// como tooltip (hover sobre el label) + drawer expandible (click en "?").
//
// Patrón: tabla central → renderers la consumen vía `getFieldDoc(entity, field)`.
// V1 cubre 4 fields demo (Recipe.name, Recipe.yield, Product.name, Product.priceCents)
// para validar el patrón. El resto se completa en otro turno (mantener este
// archivo plano + bien tipado, sin lógica fuera de los strings).

export interface FieldDoc {
  /** Una frase clara — qué es este campo desde el punto de vista del comerciante. */
  summary: string
  /** 2-3 ejemplos concretos del valor que va aquí (no genéricos). */
  examples: string[]
  /** Errores que la gente comete con este campo (cosas a evitar). */
  commonMistakes: string[]
}

/**
 * Tabla `Entity → field → FieldDoc`. Si una entrada no existe, los renderers
 * deben degradar silenciosamente (no mostrar "?" / tooltip vacío).
 */
export const FIELD_DOCS: Record<string, Record<string, FieldDoc>> = {
  Recipe: {
    name: {
      summary: 'Nombre interno de la receta. Lo verás en el escandallo y en el KDS; no se imprime en la cuenta del cliente (eso lo dicta el nombre del Producto).',
      examples: [
        'Tortilla de jamón (orden 2 piezas)',
        'Salsa madre — bechamel',
        'Café espresso doble',
      ],
      commonMistakes: [
        'Mezclar nombre comercial con receta: la receta es "Tortilla de jamón"; el producto en menú puede llamarse "Tortilla a la española".',
        'No incluir el tamaño/porcionado en el nombre cuando hay variantes (ej. "Pizza margarita CHICA" vs "Pizza margarita GRANDE") — confunde al cocinero.',
        'Usar emojis o caracteres raros: rompen búsquedas y filtros.',
      ],
    },
    yield: {
      summary: 'Rendimiento — cuántas porciones produce una corrida de esta receta. El costeo por porción se calcula como (costo total) / yield.',
      examples: [
        '1 (la receta produce 1 plato individual)',
        '10 (una olla de salsa rinde 10 servicios)',
        '40 (40 piezas por tanda)',
      ],
      commonMistakes: [
        'Confundir rendimiento con cantidad por ingrediente: si la receta dice "20 g de queso", eso es por porción, no para 20 porciones.',
        'Dejar 0 o 1 cuando en realidad rinde varias: el costo por porción saldrá inflado.',
        'Cambiar el yield sin reescalar los ingredientes — el costo deja de cuadrar.',
      ],
    },
  },
  Product: {
    name: {
      summary: 'Nombre que el cliente ve en el menú y en la cuenta impresa. Mantenlo corto, claro y sin redundancia (la categoría ya da contexto).',
      examples: [
        'Tortilla a la española',
        'Café americano',
        'Tacos al pastor (3 pzas)',
      ],
      commonMistakes: [
        'Incluir el precio en el nombre — el precio vive aparte y cambia.',
        'TODO EN MAYÚSCULAS: dificulta la lectura en menús; usa Title Case.',
        'Duplicar la categoría ("Bebida agua mineral" cuando ya está en categoría "Bebidas").',
      ],
    },
    priceCents: {
      summary: 'Precio público del producto en centavos de MXN (la UI te lo muestra en pesos para que no te equivoques). Es el precio base; las listas de precios pueden sobrescribirlo.',
      examples: [
        '$95.00 → 9500 centavos',
        '$120.00 → 12000 centavos',
        '$38.50 → 3850 centavos',
      ],
      commonMistakes: [
        'Confundir centavos con pesos: 95 ≠ $95 (sería $0.95). Aquí siempre se guarda en centavos.',
        'Olvidar que el IVA puede o no estar incluido — definirlo a nivel de catálogo, no de producto.',
        'Subir el precio uno por uno cuando hay un alza general: usa una lista de precios temporal.',
      ],
    },
  },
}

/**
 * Lookup tipado. Devuelve `null` si no hay doc → el caller decide si mostrar
 * el "?" o no (no muestres tooltip vacío).
 */
export function getFieldDoc(entity: string, field: string): FieldDoc | null {
  return FIELD_DOCS[entity]?.[field] ?? null
}
